// Paquete del Auditor (PBC). Contabilidad → Libros y Auxiliares.
//
// «PBC» es *prepared by client*: la lista de archivos que el auditor manda por
// correo antes de pisar la oficina. Hoy eso son tres días de correos, Excel
// sueltos y versiones distintas del mismo archivo — y el auditor no puede probar
// que lo que analizó es lo que produjo el sistema.
//
// La mitad del valor está en el MANIFIESTO: por cada población, cuántas filas,
// las sumas de control y un SHA-256 del contenido. Eso convierte «unos Excel que
// me mandaron» en evidencia con trazabilidad.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileSpreadsheet, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { field, rpc, usd, hoy } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Poblacion {
  clave: string; titulo: string; nota: string;
  filas: number; filas_entregadas: number; recortado: boolean;
  sumas_control: Record<string, number>; sha256: string;
}
interface Manifiesto {
  empresa: string; ruc: string; desde: string; hasta: string;
  generado_at: string; generado_por: string | null;
  poblaciones: Poblacion[];
}

const inicioAnio = `${new Date().getFullYear()}-01-01`;

export function PaqueteAuditor() {
  const [desde, setDesde] = useState(inicioAnio);
  const [hasta, setHasta] = useState(hoy);
  const [bajando, setBajando] = useState(false);

  const { data, isFetching, refetch } = useQuery<Manifiesto>({
    queryKey: ['pbc-manifiesto', desde, hasta],
    queryFn: () => rpc<Manifiesto>('cont_pbc_manifiesto', { _desde: desde, _hasta: hasta }),
  });

  const pobs = data?.poblaciones ?? [];
  const totalFilas = pobs.reduce((s, p) => s + Number(p.filas || 0), 0);
  const hayRecorte = pobs.some((p) => p.recortado);

  // Un solo libro de Excel con una hoja por población más la del manifiesto:
  // un ZIP de CSV sueltos se descomprime y se pierde; el manifiesto tiene que
  // viajar PEGADO a los datos.
  const descargar = async () => {
    if (!data) return;
    setBajando(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      // La hoja del manifiesto va PRIMERA: es lo que el auditor mira al abrir.
      const cab: (string | number)[][] = [
        ['PAQUETE DEL AUDITOR'],
        ['Empresa', data.empresa], ['RUC', data.ruc],
        ['Período', `${data.desde} a ${data.hasta}`],
        ['Generado', new Date(data.generado_at).toLocaleString(LOCALE)],
        ['Generado por', data.generado_por ?? '—'],
        [],
        ['Población', 'Filas', 'Entregadas', 'Completo', 'Sumas de control', 'SHA-256 del contenido'],
      ];
      for (const p of pobs) {
        cab.push([
          p.titulo, Number(p.filas), Number(p.filas_entregadas),
          p.recortado ? 'NO — recortado' : 'sí',
          Object.entries(p.sumas_control ?? {}).map(([k, v]) => `${k}=${v}`).join(' · '),
          p.sha256,
        ]);
      }
      cab.push([], ['El SHA-256 se calcula sobre las filas de cada población en orden estable.']);
      cab.push(['Si el contenido cambia, el sello cambia. Repita la extracción para comparar.']);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(cab), 'Manifiesto');

      for (const p of pobs) {
        const filas = await rpc<Record<string, unknown>[]>('cont_pbc_datos', {
          _poblacion: p.clave, _desde: desde, _hasta: hasta,
        });
        const ws = filas.length
          ? XLSX.utils.json_to_sheet(filas)
          : XLSX.utils.aoa_to_sheet([['(sin filas en el período)']]);
        // Excel no admite nombres de hoja de más de 31 caracteres.
        XLSX.utils.book_append_sheet(wb, ws, p.titulo.slice(0, 31));
      }

      XLSX.writeFile(wb, `paquete-auditor-${data.ruc || 'empresa'}-${desde}_${hasta}.xlsx`);
      toast.success(`Paquete generado · ${pobs.length} poblaciones, ${totalFilas.toLocaleString(LOCALE)} filas`);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBajando(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5" /> Paquete del Auditor
        </h1>
        <Button isLoading={bajando} disabled={!data || isFetching} onClick={() => void descargar()}>
          <Download className="w-4 h-4 mr-1" /> Descargar paquete
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Un solo archivo de Excel con las nueve poblaciones que pide un auditor externo, y una hoja de
        <b> manifiesto</b> con el número de filas, las sumas de control y un sello SHA-256 de cada una. Ese sello
        es lo que permite probar que el archivo entregado es el que produjo el sistema, y repetir la extracción
        meses después para compararla.
      </p>

      <Card><CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <label className="flex flex-col text-xs text-muted-foreground">Desde
          <input type="date" className={`${field} w-40 mt-1`} value={desde}
            onChange={(e) => setDesde(e.target.value)} /></label>
        <label className="flex flex-col text-xs text-muted-foreground">Hasta
          <input type="date" className={`${field} w-40 mt-1`} value={hasta}
            onChange={(e) => setHasta(e.target.value)} /></label>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          Recalcular
        </Button>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
      </CardContent></Card>

      {hayRecorte && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
          <b>El paquete no lleva la población completa.</b> Alguna tabla supera el tope de 50.000 filas y se
          entrega recortada. El manifiesto dice siempre el total real, así que el recorte queda documentado;
          acorta el período si necesitas la población entera.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" />
          Manifiesto · {pobs.length} poblaciones · {totalFilas.toLocaleString(LOCALE)} filas
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Población</th>
              <th className="py-2 pr-3 text-right">Filas</th>
              <th className="py-2 pr-3">Sumas de control</th>
              <th className="py-2 pr-3">Sello</th>
            </tr></thead>
            <tbody>
              {pobs.length === 0 && !isFetching && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">
                  Elige un período y pulsa Recalcular.
                </td></tr>
              )}
              {pobs.map((p, i) => (
                <tr key={p.clave} className="border-b last:border-0 align-top">
                  <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">
                    <div className="font-medium">{p.titulo}</div>
                    <div className="text-xs text-muted-foreground max-w-md">{p.nota}</div>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">
                    {Number(p.filas).toLocaleString(LOCALE)}
                    {p.recortado && (
                      <div className="text-xs text-amber-600">
                        se entregan {Number(p.filas_entregadas).toLocaleString(LOCALE)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs font-mono tabular-nums">
                    {Object.entries(p.sumas_control ?? {}).length === 0
                      ? <span className="text-muted-foreground">—</span>
                      : Object.entries(p.sumas_control).map(([k, v]) => (
                          <div key={k}>{k}: {usd(v)}</div>
                        ))}
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-muted-foreground break-all max-w-[14rem]"
                    title={p.sha256}>{p.sha256?.slice(0, 24)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {data && (
        <p className="text-xs text-muted-foreground">
          Generado {new Date(data.generado_at).toLocaleString(LOCALE)}
          {data.generado_por ? ` por ${data.generado_por}` : ''} · {data.empresa} · RUC {data.ruc}
        </p>
      )}
    </div>
  );
}
